'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { fetchBreakingNews, fetchNewsBySlug, viewNews, reactNews, type NewsDetail, type NewsItem } from '@/services/news'
import { fetchQuotes, type IndexQuote } from '@/services/marketApi'
import { fetchComments, createComment, reactComment, reportComment, type CommentDto } from '@/services/comments'
import { t } from '@/i18n/translations'
import BrokerRecommendations from '@/components/BrokerRecommendations'
import { usBrokers } from '@/data/indexData'

// Kategoriye göre conversion-odaklı CTA copy'si
function getBrokerCtaForCategory(category: string, lang: string) {
  if (category === 'kripto') {
    return {
      headline: lang === 'en' ? '₿ Want to ride this crypto move?' : '₿ Bu kripto hareketinden faydalanmak ister misin?',
      subline: lang === 'en'
        ? 'Open an account in minutes. Compare brokers offering crypto and start investing today — zero commission options available.'
        : 'Dakikalar içinde hesap aç. Kripto sunan aracı kurumları karşılaştır ve bugün yatırıma başla — sıfır komisyon seçenekleri mevcut.',
      cta: lang === 'en' ? 'Open a crypto account →' : 'Kripto Hesabı Aç →',
      source: 'news_detail_crypto',
    }
  }
  if (category === 'forex-emtia') {
    return {
      headline: lang === 'en' ? '💱 Trade this forex / commodity move' : '💱 Bu döviz / emtia hareketine yatırım yap',
      subline: lang === 'en'
        ? 'You need a brokerage account to trade forex and commodities. Compare 30+ trusted brokers in seconds.'
        : 'Döviz ve emtia işlemleri için bir aracı kuruma ihtiyacın var. 30+ güvenilir broker’ı saniyeler içinde karşılaştır.',
      cta: lang === 'en' ? 'Open an account →' : 'Hesap Aç →',
      source: 'news_detail_forex',
    }
  }
  return {
    headline: lang === 'en' ? '💸 Ready to act on this news?' : '💸 Bu fırsata yatırım yapmaya hazır mısın?',
    subline: lang === 'en'
      ? 'You need a brokerage account to invest. Compare 30+ trusted brokers in seconds — zero commission options available.'
      : 'Yatırım yapmak için bir aracı kuruma ihtiyacın var. 30+ güvenilir broker’ı saniyeler içinde karşılaştır — sıfır komisyon seçenekleri mevcut.',
    cta: lang === 'en' ? 'Open an account →' : 'Hesap Aç →',
    source: 'news_detail_general',
  }
}

// Strip OpenAI web_search markdown source citations: ([text](url))
function cleanContent(text: string): string {
  return text
    .replace(/\s*\(\[.*?\]\(https?:\/\/[^)]*\)\)/g, '')
    .replace(/\[([^\]]+)\]\(https?:\/\/[^)]*\)/g, '$1')
}

const categoryI18n: Record<string, string> = {
  piyasalar: 'news.markets',
  ekonomi: 'news.economy',
  bilancolar: 'news.earnings',
  dunya: 'news.world',
  enerji: 'news.energy',
  teknoloji: 'news.tech',
  kripto: 'news.crypto',
  'forex-emtia': 'news.forexCommodity',
  rehber: 'news.guide',
}

const categoryColors: Record<string, string> = {
  piyasalar: '#3498DB',
  ekonomi: '#27AE60',
  bilancolar: '#8E44AD',
  dunya: '#E67E22',
  enerji: '#E74C3C',
  teknoloji: '#2980B9',
  kripto: '#F39C12',
  'forex-emtia': '#1ABC9C',
  rehber: '#9B59B6',
}

function formatFullDate(iso: string, lang: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRelativeTime(iso: string, lang: string): string {
  const now = Date.now()
  const diff = now - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return t('time.justNow', lang)
  if (mins < 60) return `${mins} ${t('time.minsAgo', lang)}`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} ${t('time.hoursAgo', lang)}`
  const days = Math.floor(hours / 24)
  return `${days} ${t('time.daysAgo', lang)}`
}

function RelatedCard({ item, lang, isLast }: { item: NewsItem; lang: string; isLast: boolean }) {
  const catKey = categoryI18n[item.category]
  const catLabel = catKey ? t(catKey, lang) : item.category
  return (
    <Link
      href={{ pathname: '/news/[slug]', params: { slug: item.slug || item.id } }}
      className="block py-3 no-underline transition-opacity duration-150 hover:opacity-70"
      style={{
        borderBottom: isLast ? 'none' : '1px solid var(--border-color)',
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{
            backgroundColor: (categoryColors[item.category] ?? '#666') + '18',
            color: categoryColors[item.category] ?? '#666',
          }}
        >
          {catLabel}
        </span>
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          {formatRelativeTime(item.publishedAt, lang)}
        </span>
      </div>
      <h4
        className="text-[13px] font-semibold leading-snug m-0 line-clamp-2"
        style={{ color: 'var(--text-main)' }}
      >
        {item.title}
      </h4>
      <span className="text-[10px] mt-1 block" style={{ color: 'var(--text-muted)' }}>
        {item.source}
      </span>
    </Link>
  )
}

/* -- Helper functions -- */

function hashStr(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

const REACTIONS_STORAGE_KEY = 'borsaya_article_reactions'
const VIEWS_STORAGE_KEY = 'borsaya_article_views'

function getReactions(): Record<string, 'like' | 'dislike'> {
  try {
    const raw = localStorage.getItem(REACTIONS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

function saveReaction(articleId: string, type: 'like' | 'dislike' | 'none') {
  const reactions = getReactions()
  if (type === 'none') delete reactions[articleId]
  else reactions[articleId] = type
  localStorage.setItem(REACTIONS_STORAGE_KEY, JSON.stringify(reactions))
}

function getViewedIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(VIEWS_STORAGE_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch { return new Set() }
}

function addViewedId(articleId: string) {
  const set = getViewedIds()
  set.add(articleId)
  sessionStorage.setItem(VIEWS_STORAGE_KEY, JSON.stringify([...set]))
}

function formatCount(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return String(n)
}

const AVATAR_COLORS = ['#E74C3C', '#3498DB', '#27AE60', '#8E44AD', '#E67E22', '#1ABC9C', '#F39C12', '#2980B9', '#D35400', '#16A085']

const COMMENT_REACTIONS_KEY = 'borsaya_comment_reactions'

function getCommentReactions(): Record<string, 'like' | 'dislike'> {
  try {
    const raw = localStorage.getItem(COMMENT_REACTIONS_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

function saveCommentReaction(commentId: string, type: 'like' | 'dislike' | 'none') {
  const reactions = getCommentReactions()
  if (type === 'none') delete reactions[commentId]
  else reactions[commentId] = type
  localStorage.setItem(COMMENT_REACTIONS_KEY, JSON.stringify(reactions))
}

const COMMENTER_STORAGE_KEY = 'borsaya_commenter'
const REPORTS_STORAGE_KEY = 'borsaya_reported_comments'

function getStoredCommenter(): { firstName: string; lastName: string; email: string } | null {
  try {
    const raw = localStorage.getItem(COMMENTER_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveCommenter(firstName: string, lastName: string, email: string) {
  localStorage.setItem(COMMENTER_STORAGE_KEY, JSON.stringify({ firstName, lastName, email }))
}

function getReportedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(REPORTS_STORAGE_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch { return new Set() }
}

function addReportedId(commentId: string) {
  const set = getReportedIds()
  set.add(commentId)
  localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify([...set]))
}

// Total comment count including replies
function countAllComments(comments: CommentDto[]): number {
  let count = 0
  for (const c of comments) {
    count += 1 + countAllComments(c.replies)
  }
  return count
}

// Helper to update a comment in the tree by ID
function updateCommentInTree(comments: CommentDto[], id: string, updater: (c: CommentDto) => CommentDto): CommentDto[] {
  return comments.map(c => {
    if (c.id === id) return updater(c)
    if (c.replies.length > 0) return { ...c, replies: updateCommentInTree(c.replies, id, updater) }
    return c
  })
}

/* -- Related Symbols -- */
function formatPrice(price: number, currency: string | null): string {
  if (currency === 'TRY') return price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺'
  if (currency === 'USD') return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (currency === 'EUR') return '€' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (currency === 'GBP') return '£' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (currency === 'JPY') return '¥' + price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (currency ? ` ${currency}` : '')
}

function RelatedSymbols({ symbols, lang }: { symbols: string[]; lang: string }) {
  const [quotes, setQuotes] = useState<IndexQuote[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (symbols.length === 0) { setLoading(false); return }
    fetchQuotes(symbols).then(data => { setQuotes(data); setLoading(false) })
  }, [symbols])

  if (symbols.length === 0) return null

  return (
    <div className="mt-5 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
      <h3 className="text-[12px] font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
        {t('detail.relatedSymbols', lang)}
      </h3>
      <div className="flex flex-wrap gap-2">
        {loading ? (
          symbols.map(s => (
            <div
              key={s}
              className="flex items-center gap-3 px-3 py-2 rounded-lg border animate-pulse"
              style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-card)', minWidth: '140px' }}
            >
              <div className="h-4 w-12 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
              <div className="h-4 w-16 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
            </div>
          ))
        ) : (
          quotes.map(q => {
            const isUp = q.changePercent >= 0
            return (
              <Link
                key={q.symbol}
                href={{ pathname: '/market/[id]', params: { id: q.symbol.toLowerCase() } }}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border no-underline transition-all hover:shadow-sm"
                style={{
                  borderColor: 'var(--border-color)',
                  backgroundColor: 'var(--bg-card)',
                }}
                title={q.name}
              >
                <span className="text-[12px] font-bold" style={{ color: 'var(--text-main)' }}>
                  {q.symbol}
                </span>
                <span className="text-[12px] tabular-nums font-medium" style={{ color: 'var(--text-main)' }}>
                  {formatPrice(q.close, q.currency)}
                </span>
                <span
                  className="text-[11px] tabular-nums font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    color: isUp ? 'var(--chart-up)' : 'var(--chart-down)',
                    backgroundColor: isUp ? 'var(--chart-up)' + '18' : 'var(--chart-down)' + '18',
                  }}
                >
                  {isUp ? '+' : ''}{q.changePercent.toFixed(2)}%
                </span>
              </Link>
            )
          })
        )}
      </div>
    </div>
  )
}

/* -- Share Bar -- */
function ShareBar({ article, lang, copied, setCopied }: {
  article: NewsDetail
  lang: string
  copied: boolean
  setCopied: (v: boolean) => void
}) {
  const [url, setUrl] = useState('')
  useEffect(() => { setUrl(window.location.href) }, [])
  const title = article.title

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const share = (href: string) => window.open(href, '_blank', 'noopener,noreferrer')

  const items: { label: string; bg: string; icon: React.ReactNode; action: () => void }[] = [
    {
      label: 'X', bg: '#000',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>,
      action: () => share(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`)
    },
    {
      label: 'WhatsApp', bg: '#25D366',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>,
      action: () => share(`https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`)
    },
    {
      label: 'Telegram', bg: '#26A5E4',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>,
      action: () => share(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`)
    },
    {
      label: 'LinkedIn', bg: '#0A66C2',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>,
      action: () => share(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)
    },
    {
      label: 'Facebook', bg: '#1877F2',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>,
      action: () => share(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
    },
    {
      label: 'Reddit', bg: '#FF4500',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" /></svg>,
      action: () => share(`https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`)
    },
  ]

  return (
    <div className="mt-6 pt-5 border-t" style={{ borderColor: 'var(--border-color)' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] font-bold mr-1" style={{ color: 'var(--text-muted)' }}>
          {t('detail.share', lang)}
        </span>
        {items.map(item => (
          <button
            key={item.label}
            title={item.label}
            onClick={item.action}
            className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-110 active:scale-95"
            style={{ backgroundColor: item.bg, border: 'none', padding: 0 }}
          >
            {item.icon}
          </button>
        ))}
        <a
          title="E-posta"
          href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
          style={{ backgroundColor: 'var(--text-muted)', border: 'none', padding: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
        </a>
        <button
          title={copied ? (t('detail.copied', lang)) : (t('detail.copyLink', lang))}
          aria-label={copied ? (t('detail.copied', lang)) : (t('detail.copyLink', lang))}
          onClick={handleCopy}
          className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-110 active:scale-95"
          style={{ backgroundColor: copied ? '#27AE60' : 'var(--bg-secondary)', border: '1px solid var(--border-color)', padding: 0 }}
        >
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          )}
        </button>
      </div>
    </div>
  )
}

/* -- Text-to-Speech (TTS) - Chunked + Premium Voice -- */
function splitIntoChunks(text: string, maxLen = 180): string[] {
  const chunks: string[] = []
  const sentences = text.replace(/([.!?;:।])\s+/g, '$1|SPLIT|').split('|SPLIT|')
  let buf = ''
  for (const s of sentences) {
    if (buf.length + s.length > maxLen && buf.length > 0) {
      chunks.push(buf.trim())
      buf = ''
    }
    buf += (buf ? ' ' : '') + s
  }
  if (buf.trim()) chunks.push(buf.trim())
  return chunks
}

function pickBestVoice(lang: string): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices()
  const prefix = lang === 'en' ? 'en' : 'tr'
  const matching = voices.filter(v => v.lang.startsWith(prefix))
  if (matching.length === 0) return null
  const premium = matching.find(v =>
    /premium|enhanced|natural|neural|google|microsoft|yelda|cemal/i.test(v.name)
  )
  if (premium) return premium
  const network = matching.find(v => !v.localService)
  if (network) return network
  return matching[0]
}

function TextToSpeech({ title, content, lang }: { title: string; content: string; lang: string }) {
  const [state, setState] = useState<'idle' | 'playing' | 'paused'>('idle')
  const [progress, setProgress] = useState(0)
  const [isIos, setIsIos] = useState(false)
  const [showIosHint, setShowIosHint] = useState(false)
  const chunksRef = useRef<string[]>([])
  const idxRef = useRef(0)
  const stoppedRef = useRef(false)

  // iOS Safari'de Web Speech API sadece "Eloquence" (robot) sesleri veriyor;
  // Apple'ın Siri sesli "Sayfayı Dinle" özelliğine yönlendirmek tek kaliteli yol.
  useEffect(() => {
    if (typeof navigator === 'undefined') return
    const ua = navigator.userAgent
    const detected =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    setIsIos(detected)
  }, [])

  const stop = useCallback(() => {
    stoppedRef.current = true
    window.speechSynthesis.cancel()
    chunksRef.current = []
    idxRef.current = 0
    setProgress(0)
    setState('idle')
  }, [])

  useEffect(() => {
    return () => { stoppedRef.current = true; window.speechSynthesis.cancel() }
  }, [title, content, lang])

  // Preload voices (some browsers load async)
  useEffect(() => {
    window.speechSynthesis.getVoices()
    const h = () => window.speechSynthesis.getVoices()
    window.speechSynthesis.addEventListener('voiceschanged', h)
    return () => window.speechSynthesis.removeEventListener('voiceschanged', h)
  }, [])

  const speakChunk = useCallback((chunks: string[], idx: number) => {
    if (stoppedRef.current || idx >= chunks.length) {
      if (!stoppedRef.current) { setState('idle'); setProgress(0) }
      return
    }
    const utter = new SpeechSynthesisUtterance(chunks[idx])
    utter.lang = lang === 'en' ? 'en-US' : 'tr-TR'
    utter.rate = 0.95
    utter.pitch = 1.0
    const voice = pickBestVoice(lang)
    if (voice) utter.voice = voice
    utter.onend = () => {
      const next = idx + 1
      idxRef.current = next
      setProgress(Math.round((next / chunks.length) * 100))
      speakChunk(chunks, next)
    }
    utter.onerror = () => { if (!stoppedRef.current) { setState('idle'); setProgress(0) } }
    window.speechSynthesis.speak(utter)
  }, [lang])

  const play = () => {
    // iOS Safari: Apple Web Speech API'sini Eloquence (robot) seslere kilitliyor.
    // Web Speech'i tetiklemek yerine kullanıcıyı Siri sesli "Sayfayı Dinle"ye yönlendiriyoruz.
    if (isIos) {
      setShowIosHint(true)
      return
    }
    if (state === 'paused') {
      window.speechSynthesis.resume()
      setState('playing')
      return
    }
    stop()
    stoppedRef.current = false
    const plainText = (content ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    const fullText = title + '. ' + plainText
    const chunks = splitIntoChunks(fullText)
    chunksRef.current = chunks
    idxRef.current = 0
    setProgress(0)
    setState('playing')
    speakChunk(chunks, 0)
  }

  const pause = () => {
    window.speechSynthesis.pause()
    setState('paused')
  }

  const btnBase = 'flex items-center gap-1.5 text-[12px] font-medium cursor-pointer transition-opacity hover:opacity-80'
  const btnStyle: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)' }

  return (
    <div>
      <div className="flex items-center gap-2">
      {state === 'idle' && (
        <button className={btnBase} style={btnStyle} onClick={play} title={t('detail.listen', lang)}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </svg>
          {t('detail.listen', lang)}
        </button>
      )}

      {state === 'playing' && (
        <>
          <button className={btnBase} style={{ ...btnStyle, color: 'var(--accent-color)' }} onClick={pause}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
            {t('detail.pause', lang)}
          </button>
          <button className={btnBase} style={btnStyle} onClick={stop}>
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
            {t('detail.stop', lang)}
          </button>
          {progress > 0 && (
            <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>%{progress}</span>
          )}
        </>
      )}

      {state === 'paused' && (
        <>
          <button className={btnBase} style={{ ...btnStyle, color: 'var(--accent-color)' }} onClick={play}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            {t('detail.resume', lang)}
          </button>
          <button className={btnBase} style={btnStyle} onClick={stop}>
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
            {t('detail.stop', lang)}
          </button>
          {progress > 0 && (
            <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>%{progress}</span>
          )}
        </>
      )}
      </div>

      {isIos && showIosHint && (
        <div
          className="mt-3 p-3.5 rounded-lg border text-[12px] relative"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          <button
            onClick={() => setShowIosHint(false)}
            aria-label={lang === 'en' ? 'Close' : 'Kapat'}
            className="absolute cursor-pointer"
            style={{ top: 6, right: 6, background: 'none', border: 'none', padding: 4, color: 'var(--text-muted)' }}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="flex items-center gap-2 mb-1.5 pr-7 font-semibold" style={{ color: 'var(--text-main)' }}>
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
            <span>{lang === 'en' ? 'Listen with Siri voice' : 'Siri sesi ile dinle'}</span>
          </div>
          <div className="leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            {lang === 'en' ? (
              <>Tap the <b style={{ color: 'var(--text-main)' }}>AA</b> icon in Safari&apos;s address bar, then choose <b style={{ color: 'var(--text-main)' }}>Listen to Page</b>. Apple Siri reads the article in high quality &mdash; available on iOS 17+.</>
            ) : (
              <>Safari&apos;de adres çubuğundaki <b style={{ color: 'var(--text-main)' }}>AA</b> simgesine dokun, ardından <b style={{ color: 'var(--text-main)' }}>Sayfayı Dinle</b> seçeneğine bas. Apple Siri Türkçe sesi ile haberi yüksek kalitede dinlersin &mdash; iOS 17+ özelliğidir.</>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* -- Engagement Bar -- */
function EngagementBar({ views, likes, dislikes, liked, disliked, onLike, onDislike }: {
  // U4: beğen/beğenme butonları ekran okuyucuya yalnızca sayıyı ("12, düğme")
  // okutuyordu; erişilebilir ad ve basılı durumu için locale gerekiyor.
  articleId: string
  views: number
  likes: number
  dislikes: number
  liked: boolean
  disliked: boolean
  onLike: () => void
  onDislike: () => void
}) {
  const lang = useLocale()
  return (
    <div
      className="mt-6 pt-5 pb-5 border-t border-b flex items-center gap-6"
      style={{ borderColor: 'var(--border-color)' }}
    >
      {/* Views */}
      <span className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        <span className="text-[13px] tabular-nums">{formatCount(views)}</span>
      </span>

      {/* Like */}
      <button
        className="flex items-center gap-1.5 transition-colors cursor-pointer"
        style={{
          color: liked ? '#27AE60' : 'var(--text-muted)',
          background: 'none', border: 'none', padding: 0,
        }}
        onClick={onLike}
        aria-label={lang === 'en' ? 'Like' : 'Beğen'}
        aria-pressed={liked}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
        <span className="text-[13px] tabular-nums font-medium">{formatCount(likes)}</span>
      </button>

      {/* Dislike */}
      <button
        className="flex items-center gap-1.5 transition-colors cursor-pointer"
        style={{
          color: disliked ? '#E74C3C' : 'var(--text-muted)',
          background: 'none', border: 'none', padding: 0,
        }}
        onClick={onDislike}
        aria-label={lang === 'en' ? 'Dislike' : 'Beğenme'}
        aria-pressed={disliked}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill={disliked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/></svg>
        <span className="text-[13px] tabular-nums font-medium">{formatCount(dislikes)}</span>
      </button>
    </div>
  )
}

/* -- Comment Card (like/dislike + reply + report) -- */
function CommentCard({ comment, lang, depth, reportedIds, commentReactions, onReact, onReply, onReport, commenter }: {
  comment: CommentDto
  lang: string
  depth: number
  reportedIds: Set<string>
  commentReactions: Record<string, 'like' | 'dislike'>
  onReact: (commentId: string, type: 'like' | 'dislike') => void
  onReply: (parentId: string, text: string, replyCommenter?: { firstName: string; lastName: string; email: string }) => void
  onReport: (commentId: string) => void
  commenter: { firstName: string; lastName: string; email: string } | null
}) {
  const [replyOpen, setReplyOpen] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replyFirstName, setReplyFirstName] = useState('')
  const [replyLastName, setReplyLastName] = useState('')
  const [replyEmail, setReplyEmail] = useState('')
  const [reportOpen, setReportOpen] = useState(false)
  const [justReported, setJustReported] = useState(false)
  const initials = (comment.firstName[0] + comment.lastName[0]).toUpperCase()
  const colorIdx = hashStr(comment.firstName + comment.lastName) % AVATAR_COLORS.length
  const isReported = reportedIds.has(comment.id) || comment.isReported
  const myReaction = commentReactions[comment.id] as 'like' | 'dislike' | undefined
  const maxDepth = 6

  const handleReply = () => {
    if (!replyText.trim()) return
    if (commenter) {
      onReply(comment.id, replyText.trim())
    } else {
      if (!replyFirstName.trim() || !replyLastName.trim() || !replyEmail.trim()) return
      onReply(comment.id, replyText.trim(), { firstName: replyFirstName.trim(), lastName: replyLastName.trim(), email: replyEmail.trim() })
    }
    setReplyText('')
    setReplyOpen(false)
  }

  const handleReport = () => {
    onReport(comment.id)
    setReportOpen(false)
    setJustReported(true)
    setTimeout(() => setJustReported(false), 2500)
  }

  const reportReasons = [
    { key: 'detail.reportSpam', value: 'spam' },
    { key: 'detail.reportOffensive', value: 'offensive' },
    { key: 'detail.reportMisleading', value: 'misleading' },
    { key: 'detail.reportAd', value: 'ad' },
    { key: 'detail.reportOther', value: 'other' },
  ]

  return (
    <div style={{ marginLeft: depth > 0 ? `min(${Math.min(depth, maxDepth) * 24}px, ${Math.min(depth, maxDepth) * 3}vw)` : undefined }}>
      <div className="flex gap-2 sm:gap-3 py-3">
        {/* Avatar */}
        <div
          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-[11px] font-bold text-white shrink-0"
          style={{ backgroundColor: AVATAR_COLORS[colorIdx] }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          {/* Name + time */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[12px] font-semibold" style={{ color: 'var(--text-main)' }}>
              {comment.firstName} {comment.lastName.charAt(0)}.
            </span>
            <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {formatRelativeTime(comment.createdAt, lang)}
            </span>
          </div>

          {/* Comment text */}
          <p className="text-[13px] leading-relaxed m-0" style={{ color: 'var(--text-main)' }}>{comment.text}</p>

          {/* Like / Dislike / Reply / Report */}
          <div className="flex items-center gap-4 mt-1.5">
            {/* Like */}
            <button
              className="flex items-center gap-1 transition-colors cursor-pointer"
              style={{
                color: myReaction === 'like' ? '#27AE60' : 'var(--text-muted)',
                background: 'none', border: 'none', padding: 0,
              }}
              onClick={() => onReact(comment.id, 'like')}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={myReaction === 'like' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
              <span className="text-[11px] tabular-nums">{comment.likes}</span>
            </button>

            {/* Dislike */}
            <button
              className="flex items-center gap-1 transition-colors cursor-pointer"
              style={{
                color: myReaction === 'dislike' ? '#E74C3C' : 'var(--text-muted)',
                background: 'none', border: 'none', padding: 0,
              }}
              onClick={() => onReact(comment.id, 'dislike')}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={myReaction === 'dislike' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/></svg>
              <span className="text-[11px] tabular-nums">{comment.dislikes}</span>
            </button>

            {/* Reply */}
            {depth < maxDepth && (
              <button
                className="text-[11px] font-medium cursor-pointer hover:underline"
                style={{ color: 'var(--text-muted)', background: 'none', border: 'none', padding: 0 }}
                onClick={() => setReplyOpen(!replyOpen)}
              >
                {t('detail.reply', lang)}
              </button>
            )}

            {/* Report */}
            <div className="relative ml-auto">
              {justReported ? (
                <span className="flex items-center gap-1 text-[11px] font-medium" style={{ color: '#27AE60' }}>
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {t('detail.reported', lang)}
                </span>
              ) : isReported ? (
                <span className="text-[10px]" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
                  {t('detail.reported', lang)}
                </span>
              ) : (
                <button
                  className="flex items-center gap-1 cursor-pointer transition-opacity hover:opacity-70"
                  style={{ color: 'var(--text-muted)', background: 'none', border: 'none', padding: 0 }}
                  onClick={() => setReportOpen(!reportOpen)}
                  title={t('detail.report', lang)}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                </button>
              )}

              {/* Report dropdown */}
              {reportOpen && (
                <>
                  {/* Backdrop */}
                  <div className="fixed inset-0 z-40" onClick={() => setReportOpen(false)} />
                  <div
                    className="absolute right-0 top-6 z-50 rounded-lg border shadow-lg py-1 min-w-[180px]"
                    style={{
                      backgroundColor: 'var(--bg-card)',
                      borderColor: 'var(--border-color)',
                    }}
                  >
                    <div
                      className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {t('detail.report', lang)}
                    </div>
                    {reportReasons.map(r => (
                      <button
                        key={r.value}
                        className="w-full text-left px-3 py-2 text-[12px] cursor-pointer transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ color: 'var(--text-main)', background: 'none', border: 'none' }}
                        onClick={() => handleReport()}
                      >
                        {t(r.key, lang)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Reply form (inline) */}
          {replyOpen && (
            <div className="mt-2 space-y-2">
              {!commenter && (
                <div className="flex gap-2">
                  <input type="text" placeholder={t('detail.firstName', lang)} value={replyFirstName} onChange={e => setReplyFirstName(e.target.value)}
                    className="flex-1 px-3 py-1.5 rounded-lg border text-[12px] outline-none focus:ring-1 focus:ring-[var(--accent-color)]"
                    style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-body)', color: 'var(--text-main)' }} maxLength={50} />
                  <input type="text" placeholder={t('detail.lastName', lang)} value={replyLastName} onChange={e => setReplyLastName(e.target.value)}
                    className="flex-1 px-3 py-1.5 rounded-lg border text-[12px] outline-none focus:ring-1 focus:ring-[var(--accent-color)]"
                    style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-body)', color: 'var(--text-main)' }} maxLength={50} />
                  <input type="email" placeholder={t('detail.email', lang)} value={replyEmail} onChange={e => setReplyEmail(e.target.value)}
                    className="flex-1 px-3 py-1.5 rounded-lg border text-[12px] outline-none focus:ring-1 focus:ring-[var(--accent-color)]"
                    style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-body)', color: 'var(--text-main)' }} maxLength={100} />
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t('detail.writeComment', lang)}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleReply() }}
                  className="flex-1 px-3 py-1.5 rounded-lg border text-[12px] outline-none focus:ring-1 focus:ring-[var(--accent-color)]"
                  style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-body)', color: 'var(--text-main)' }}
                  maxLength={500}
                  autoFocus
                />
                <button
                  type="button"
                  disabled={!replyText.trim() || (!commenter && (!replyFirstName.trim() || !replyLastName.trim() || !replyEmail.trim()))}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-white shrink-0"
                  style={{
                    backgroundColor: replyText.trim() ? 'var(--accent-color)' : 'var(--border-color)',
                    cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                    border: 'none',
                  }}
                  onClick={handleReply}
                >
                  {t('detail.reply', lang)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Child comments */}
      {comment.replies.map(reply => (
        <CommentCard
          key={reply.id}
          comment={reply}
          lang={lang}
          depth={depth + 1}
          reportedIds={reportedIds}
          commentReactions={commentReactions}
          onReact={onReact}
          onReply={onReply}
          onReport={onReport}
          commenter={commenter}
        />
      ))}
    </div>
  )
}

/* -- Comments Section -- */
function CommentsSection({ articleId, lang }: { articleId: string; lang: string }) {
  const [comments, setComments] = useState<CommentDto[]>([])
  const [reportedIds, setReportedIds] = useState<Set<string>>(() => getReportedIds())
  const [commentReactions, setCommentReactions] = useState<Record<string, 'like' | 'dislike'>>(() => getCommentReactions())
  const [commenter, setCommenter] = useState(getStoredCommenter)
  const [firstName, setFirstName] = useState(commenter?.firstName ?? '')
  const [lastName, setLastName] = useState(commenter?.lastName ?? '')
  const [email, setEmail] = useState(commenter?.email ?? '')
  const [text, setText] = useState('')
  const [success, setSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Fetch comments from API
  useEffect(() => {
    fetchComments(articleId).then(setComments)
  }, [articleId])

  const isRegistered = !!commenter
  const canSubmit = !submitting && text.trim().length > 0 && (isRegistered || (firstName.trim() && lastName.trim() && email.trim()))
  const totalComments = countAllComments(comments)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    const fn = isRegistered ? commenter!.firstName : firstName.trim()
    const ln = isRegistered ? commenter!.lastName : lastName.trim()
    const em = isRegistered ? commenter!.email : email.trim()

    if (!isRegistered) {
      saveCommenter(fn, ln, em)
      setCommenter({ firstName: fn, lastName: ln, email: em })
    }

    setSubmitting(true)
    const result = await createComment({
      newsId: articleId,
      firstName: fn,
      lastName: ln,
      email: em,
      text: text.trim(),
    })
    setSubmitting(false)

    if (result) {
      setComments(prev => [result, ...prev])
      setText('')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    }
  }

  const handleReact = async (commentId: string, clickedType: 'like' | 'dislike') => {
    const previous = commentReactions[commentId] || 'none'
    const newType = previous === clickedType ? 'none' : clickedType
    const result = await reactComment(commentId, newType, previous)
    if (result) {
      saveCommentReaction(commentId, newType)
      setCommentReactions(prev => {
        const next = { ...prev }
        if (newType === 'none') delete next[commentId]
        else next[commentId] = newType
        return next
      })
      setComments(old => updateCommentInTree(old, commentId, c => ({
        ...c,
        likes: result.likes,
        dislikes: result.dislikes,
      })))
    }
  }

  const handleReply = async (parentId: string, replyText: string, replyCommenter?: { firstName: string; lastName: string; email: string }) => {
    const c = commenter ?? replyCommenter
    if (!c) return
    if (!commenter && replyCommenter) {
      saveCommenter(replyCommenter.firstName, replyCommenter.lastName, replyCommenter.email)
      setCommenter(replyCommenter)
    }
    const result = await createComment({
      newsId: articleId,
      parentCommentId: parentId,
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email,
      text: replyText,
    })
    if (result) {
      setComments(old => updateCommentInTree(old, parentId, c => ({
        ...c,
        replies: [...c.replies, result],
      })))
    }
  }

  const handleReport = async (commentId: string) => {
    const ok = await reportComment(commentId)
    if (ok) {
      addReportedId(commentId)
      setReportedIds(prev => new Set([...prev, commentId]))
    }
  }

  const inputStyle = {
    borderColor: 'var(--border-color)',
    backgroundColor: 'var(--bg-body)',
    color: 'var(--text-main)',
  }

  return (
    <div className="mt-6 pt-5" style={{ borderTop: 'none' }}>
      {/* Title */}
      <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--text-main)' }}>
        {t('detail.comments', lang)} ({totalComments})
      </h3>

      {/* Comment form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-card)' }}>
          {/* Top: user info or registration fields */}
          {!isRegistered ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <input
                type="text"
                placeholder={t('detail.firstName', lang)}
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="px-3.5 py-2.5 text-[13px] outline-none border-b sm:border-b-0 sm:border-r"
                style={{ ...inputStyle, borderColor: 'var(--border-color)', backgroundColor: 'transparent' }}
                maxLength={50}
              />
              <input
                type="text"
                placeholder={t('detail.lastName', lang)}
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="px-3.5 py-2.5 text-[13px] outline-none border-b sm:border-b-0 sm:border-r"
                style={{ ...inputStyle, borderColor: 'var(--border-color)', backgroundColor: 'transparent' }}
                maxLength={50}
              />
              <input
                type="email"
                placeholder={t('detail.email', lang)}
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="px-3.5 py-2.5 text-[13px] outline-none"
                style={{ ...inputStyle, backgroundColor: 'transparent' }}
                maxLength={100}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3.5 py-2.5 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                style={{ backgroundColor: AVATAR_COLORS[hashStr(commenter!.firstName + commenter!.lastName) % AVATAR_COLORS.length] }}
              >
                {(commenter!.firstName[0] + commenter!.lastName[0]).toUpperCase()}
              </div>
              <span className="text-[12px] font-medium" style={{ color: 'var(--text-main)' }}>
                {commenter!.firstName} {commenter!.lastName.charAt(0)}.
              </span>
            </div>
          )}

          {/* Textarea */}
          <textarea
            placeholder={t('detail.writeComment', lang)}
            value={text}
            onChange={e => setText(e.target.value)}
            rows={3}
            className="w-full px-3.5 py-3 text-[13px] outline-none resize-none"
            style={{ ...inputStyle, border: 'none', backgroundColor: 'transparent' }}
            maxLength={1000}
          />

          {/* Bottom bar: char count + submit button */}
          <div className="flex items-center justify-between px-3.5 py-2.5 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
              {text.length}/1000
            </span>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-5 py-1.5 rounded-lg text-[12px] font-semibold text-white transition-all"
              style={{
                backgroundColor: canSubmit ? 'var(--accent-color)' : 'var(--border-color)',
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                border: 'none',
              }}
            >
              {t('detail.submitComment', lang)}
            </button>
          </div>
        </div>

        {/* Success message */}
        {success && (
          <p className="text-[12px] mt-2 font-medium" style={{ color: '#27AE60' }}>
            {t('detail.commentSuccess', lang)}
          </p>
        )}
      </form>

      {/* Comment list */}
      {comments.length > 0 ? (
        <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
          {comments.map(c => (
            <CommentCard
              key={c.id}
              comment={c}
              lang={lang}
              depth={0}
              reportedIds={reportedIds}
              commentReactions={commentReactions}
              onReact={handleReact}
              onReply={handleReply}
              onReport={handleReport}
              commenter={commenter}
            />
          ))}
        </div>
      ) : (
        <p className="text-[13px] text-center py-6" style={{ color: 'var(--text-muted)' }}>
          {t('detail.noComments', lang)}
        </p>
      )}
    </div>
  )
}

/* ============================================================
   Main Client Component
   ============================================================ */

interface NewsDetailClientProps {
  article: NewsDetail
}

export default function NewsDetailClient({ article: initialArticle }: NewsDetailClientProps) {
  const lang = useLocale()
  const [article, setArticle] = useState<NewsDetail>(initialArticle)
  const [related, setRelated] = useState<NewsItem[]>([])
  const [liked, setLiked] = useState(false)
  const [disliked, setDisliked] = useState(false)
  const [copied, setCopied] = useState(false)

  // Load reaction state from localStorage + increment view + fetch fresh counts + fetch related news
  useEffect(() => {
    // localStorage reaction state
    const reactions = getReactions()
    const reaction = reactions[article.id]
    if (reaction === 'like') setLiked(true)
    else if (reaction === 'dislike') setDisliked(true)

    // Fetch fresh engagement counts to override stale ISR cache
    fetchNewsBySlug(article.slug, lang).then(fresh => {
      if (fresh) {
        setArticle(prev => ({ ...prev, likes: fresh.likes, dislikes: fresh.dislikes, views: fresh.views }))
      }
    })

    // View count (once per session)
    const viewed = getViewedIds()
    if (!viewed.has(article.id)) {
      addViewedId(article.id)
      viewNews(article.id).then(result => {
        if (result) {
          setArticle(prev => ({ ...prev, views: result.views }))
        }
      })
    }

    // Fetch related news
    fetchBreakingNews(lang).then(breaking => {
      const currentSlug = article.slug
      const others = breaking
        .filter(n => n.slug !== currentSlug)
        .filter(n => n.category === article.category)
        .slice(0, 7)
      if (others.length < 7) {
        const rest = breaking
          .filter(n => n.slug !== currentSlug && !others.find(o => o.id === n.id))
          .slice(0, 7 - others.length)
        others.push(...rest)
      }
      setRelated(others)
    })
  }, [article.id, article.slug, article.category, lang])

  const handleLike = async () => {
    const previous = liked ? 'like' : disliked ? 'dislike' : 'none'
    const type = liked ? 'none' : 'like'
    const result = await reactNews(article.id, type, previous)
    if (result) {
      setArticle(prev => ({ ...prev, likes: result.likes, dislikes: result.dislikes }))
      setLiked(type === 'like')
      setDisliked(false)
      saveReaction(article.id, type as 'like' | 'dislike' | 'none')
    }
  }

  const handleDislike = async () => {
    const previous = liked ? 'like' : disliked ? 'dislike' : 'none'
    const type = disliked ? 'none' : 'dislike'
    const result = await reactNews(article.id, type, previous)
    if (result) {
      setArticle(prev => ({ ...prev, likes: result.likes, dislikes: result.dislikes }))
      setLiked(false)
      setDisliked(type === 'dislike')
      saveReaction(article.id, type as 'like' | 'dislike' | 'none')
    }
  }

  const catColor = categoryColors[article.category] ?? '#666'
  const catKey = categoryI18n[article.category]
  const catLabel = catKey ? t(catKey, lang) : article.category

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col lg:flex-row gap-8">

        {/* Left: Article */}
        <article className="flex-1 min-w-0">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-[12px] mb-5 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
            <Link href="/" className="no-underline hover:underline shrink-0" style={{ color: 'var(--text-muted)' }}>
              {t('nav.home', lang)}
            </Link>
            <span className="shrink-0">/</span>
            <Link href="/news" className="no-underline hover:underline shrink-0" style={{ color: 'var(--text-muted)' }}>{t('news.title', lang)}</Link>
            <span className="shrink-0">/</span>
            <span className="truncate" style={{ color: catColor }}>{catLabel}</span>
          </nav>


          {/* Title */}
          <h1
            className="text-[30px] sm:text-[38px] lg:text-[44px] font-black leading-[1.08] mb-4"
            style={{
              color: 'var(--text-main)',
              fontFamily: 'var(--font-serif-display)',
              letterSpacing: '-0.01em',
            }}
          >
            {article.title}
          </h1>

          {/* Summary / Deck */}
          <p
            className="text-[17px] sm:text-[19px] leading-[1.6] mb-6 font-normal"
            style={{
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-serif-body)',
            }}
          >
            {cleanContent(article.summary)}
          </p>

          {/* Meta info */}
          <div
            className="flex flex-wrap items-center gap-3 pb-5 mb-6 border-b text-[12px]"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}
          >
            {/* Byline — yayıncı kurum. (Eskiden isimli bir insan editör gösteriliyordu;
                Ağustos 2026 denetiminde kurum adına çevrildi.) */}
            <div className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="font-semibold" style={{ color: 'var(--text-main)' }}>
                {t('news.byline', lang)}
              </span>
            </div>

            <span style={{ color: 'var(--border-color)' }}>|</span>

            {/* Source */}
            <div className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{article.source}</span>
            </div>

            <span style={{ color: 'var(--border-color)' }}>|</span>

            {/* Date */}
            <div className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{formatFullDate(article.publishedAt, lang)}</span>
            </div>

            <span style={{ color: 'var(--border-color)' }}>|</span>

            {/* Read time */}
            <div className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
              <span>{Math.max(1, Math.ceil((article.content?.length ?? 0) / 1000))} {t('detail.readMin', lang)}</span>
            </div>

            <span style={{ color: 'var(--border-color)' }}>|</span>

            {/* Text-to-Speech */}
            <TextToSpeech title={article.title} content={article.content ?? ''} lang={lang} />
          </div>

          {/* Hero Image — body'nin hemen üstünde, varsa göster */}
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full rounded-xl mb-6"
              loading="lazy"
            />
          )}

          {/* Content */}
          <div
            className="text-[17px] leading-[1.9] mb-8"
            style={{
              color: 'var(--text-main)',
              overflowWrap: 'anywhere',
              fontFamily: 'var(--font-serif-body)',
            }}
          >
            {cleanContent(article.content ?? '').split('\n\n').map((block, i) => {
              const lines = block.split('\n')
              const isNumberedList = lines.length > 1 && lines.every(l => /^\d+[\.\)]\s/.test(l.trim()) || l.trim() === '')
              const isBulletList = lines.length > 1 && lines.every(l => /^[-\u2022]\s/.test(l.trim()) || l.trim() === '')
              const isHeading = lines.length === 1 && lines[0].trim().endsWith(':') && lines[0].trim().length < 80

              if (isHeading) {
                return (
                  <h3
                    key={i}
                    className="font-bold text-[20px] mt-8 mb-3"
                    style={{ color: 'var(--text-main)', fontFamily: 'var(--font-serif-display)' }}
                  >
                    {lines[0].trim()}
                  </h3>
                )
              }
              if (isNumberedList) {
                return (
                  <ol key={i} className="list-decimal list-inside mb-5 space-y-2">
                    {lines.filter(l => l.trim()).map((l, j) => (
                      <li key={j}>{l.replace(/^\d+[\.\)]\s*/, '')}</li>
                    ))}
                  </ol>
                )
              }
              if (isBulletList) {
                return (
                  <ul key={i} className="list-disc list-inside mb-5 space-y-2">
                    {lines.filter(l => l.trim()).map((l, j) => (
                      <li key={j}>{l.replace(/^[-\u2022]\s*/, '')}</li>
                    ))}
                  </ul>
                )
              }
              return (
                <p key={i} className="mb-5 last:mb-0">
                  {lines.map((line, j) => (
                    <span key={j}>{j > 0 && <br />}{line}</span>
                  ))}
                </p>
              )
            })}
          </div>


          {/* Tags — Ağustos 2026 denetimi: etiketler backend'de YALNIZCA Türkçe
              üretiliyor ("Yarı İletken", "Güney Kore Borsası") ve İngilizce makalede
              de aynen basılıyordu. İngilizce etiket üretimi eklenene kadar bu dilde
              gösterilmez; yanlış dilde etiket göstermektense hiç göstermemek doğru. */}
          {lang !== 'en' && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-4 mt-2">
              {article.tags.map(tag => (
                <span
                  key={tag}
                  className="text-[10px] px-2 py-0.5 rounded"
                  style={{
                    color: 'var(--text-muted)',
                    opacity: 0.55,
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Related Symbols */}
          {article.relatedSymbols && article.relatedSymbols.length > 0 && (
            <RelatedSymbols symbols={article.relatedSymbols} lang={lang} />
          )}

          {/* Share Bar */}
          <ShareBar article={article} lang={lang} copied={copied} setCopied={setCopied} />

          {/* Engagement Bar */}
          <EngagementBar
            articleId={article.id}
            views={article.views}
            likes={article.likes}
            dislikes={article.dislikes}
            liked={liked}
            disliked={disliked}
            onLike={handleLike}
            onDislike={handleDislike}
          />

          {/* Brokers CTA — kategoriye göre dinamik */}
          {(() => {
            const cta = getBrokerCtaForCategory(article.category, lang)
            return (
              <div className="mt-6">
                <BrokerRecommendations
                  brokers={usBrokers}
                  source={cta.source}
                  customHeadline={cta.headline}
                  customSubline={cta.subline}
                  customCta={cta.cta}
                />
              </div>
            )
          })()}

          {/* Comments Section */}
          <CommentsSection articleId={article.id} lang={lang} />

        </article>

        {/* Right: Related News Sidebar */}
        <aside className="w-full lg:w-80 shrink-0">
          <div
            className="lg:sticky lg:top-4 rounded-xl border p-5"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-color)',
            }}
          >
            <h3
              className="text-sm font-bold uppercase tracking-wider mb-4"
              style={{ color: 'var(--text-main)' }}
            >
              {t('detail.relatedNews', lang)}
            </h3>
            <div className="flex flex-col">
              {related.length > 0 ? (
                related.map((item, i) => <RelatedCard key={item.id} item={item} lang={lang} isLast={i === related.length - 1} />)
              ) : (
                <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                  {t('detail.noRelated', lang)}
                </p>
              )}
            </div>

            {/* All news link */}
            <Link
              href="/news"
              className="block mt-4 text-center text-[12px] font-medium no-underline py-2 rounded-lg transition-opacity hover:opacity-80"
              style={{ color: 'var(--accent-color)' }}
            >
              {t('detail.viewAll', lang)}
            </Link>
          </div>
        </aside>
      </div>
    </main>
  )
}
